import type { ArrivalView, ObjectiveView } from "./api";

export interface OperationPhase {
  number: number;
  name: string;
  trigger: string;
}

const held = (objectives: ObjectiveView[], names: string[]) =>
  names.every((name) => objectives.some((o) => o.name === name && o.mine));

/** The six visible operational phases. Husky follows its historical calendar;
 * when the clock has not reached the client yet, keep the opening/ground
 * fallback so the map never loses its useful next-objective readout. */
export function operationPhase(
  arrivals: ArrivalView[],
  objectives: ObjectiveView[],
  gameTimeS?: number | null,
): OperationPhase {
  if (gameTimeS !== null && gameTimeS !== undefined) {
    const day = Math.floor((gameTimeS - Date.UTC(1943, 6, 10) / 1000) / 86_400);
    if (day < 1) return { number: 1, name: "Invasion", trigger: "9–10 July: prevent the beachheads" };
    if (day < 6) return { number: 2, name: "Counterattack", trigger: "11–15 July: contain the Allied lodgements" };
    if (day < 15) return { number: 3, name: "Breakout", trigger: "16–24 July: delay the advance" };
    if (day < 27) return { number: 4, name: "Withdrawal", trigger: "25 July–5 August: hold the Etna–Catania line" };
    if (day < 32) return { number: 5, name: "Evacuation preparation", trigger: "6–10 August: Germans at Axis-held Messina evacuate" };
    return { number: 6, name: "Evacuation", trigger: "11–17 August: evacuate through Axis-held Messina" };
  }
  const air = arrivals.find((a) => a.opening && a.mode === "air");
  if (air) return { number: 1, name: "Axis setup", trigger: `Airborne landings at ${formatCampaignTime(air.at_game_s)}` };

  const sea = arrivals.find((a) => a.opening && a.mode === "sea");
  if (sea) return { number: 2, name: "Airborne assault", trigger: `Amphibious landings at ${formatCampaignTime(sea.at_game_s)}` };

  if (!held(objectives, ["Gela", "Licata", "Siracusa"])) {
    return { number: 3, name: "Secure the beachheads", trigger: "Hold Gela, Licata and Siracusa" };
  }
  if (!held(objectives, ["Palermo"])) {
    return { number: 4, name: "Breakout across Sicily", trigger: "Capture Palermo" };
  }
  if (!held(objectives, ["Troina", "Adrano", "Randazzo"])) {
    return { number: 5, name: "Break the Etna Line", trigger: "Hold Troina, Adrano and Randazzo" };
  }
  return {
    number: 6,
    name: "Race to Messina",
    trigger: held(objectives, ["Messina"]) ? "Messina secured" : "Capture Messina",
  };
}

export function formatCampaignTime(gameTimeS: number): string {
  const d = new Date(gameTimeS * 1000);
  const day = d.getUTCDate();
  const month = d.toLocaleString("en-GB", { month: "short", timeZone: "UTC" });
  const year = d.getUTCFullYear();
  const hh = String(d.getUTCHours()).padStart(2, "0");
  const mm = String(d.getUTCMinutes()).padStart(2, "0");
  return `${day} ${month} ${year} · ${hh}:${mm}`;
}
